/** Juice: pop bursts, sparkles, confetti, floating text and screen shake (reduced-motion aware). */
import * as Phaser from 'phaser';
import { app } from '../game/context.js';
import { FONT, ITEM_COLORS, css } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import { deco, instant, reducedMotion } from './motion.js';

type Emitter = Phaser.GameObjects.Particles.ParticleEmitter;

export class Fx {
  private dots: Emitter;
  private sparks: Emitter;
  private confettiEm: Emitter;

  constructor(private scene: Phaser.Scene) {
    const art = app().art;
    const u = vp.u;
    const dot = art.get('vfx_dot');
    const spark = art.get('vfx_sparkle');
    // real confetti art is pre-coloured; the fallback is a white strip tinted per particle
    const realConfetti = art.has('vfx_confetti_a');
    const conf = art.get(realConfetti ? 'vfx_confetti_a' : 'fx_confetti');
    this.dots = scene.add.particles(0, 0, dot.texture, {
      frame: dot.frame,
      lifespan: { min: 280, max: 520 },
      speed: { min: 140 * u, max: 420 * u },
      scale: { start: 0.55 * u, end: 0 },
      gravityY: 700 * u,
      emitting: false,
    });
    this.sparks = scene.add.particles(0, 0, spark.texture, {
      frame: spark.frame,
      lifespan: { min: 380, max: 700 },
      speed: { min: 40 * u, max: 180 * u },
      scale: { start: 0.55 * u, end: 0 },
      rotate: { min: 0, max: 180 },
      alpha: { start: 1, end: 0 },
      emitting: false,
    });
    this.confettiEm = scene.add.particles(0, 0, conf.texture, {
      frame: conf.frame,
      lifespan: { min: 1100, max: 1900 },
      speed: { min: 260 * u, max: 700 * u },
      angle: { min: 220, max: 320 },
      scale: { min: 0.5 * u, max: 0.9 * u },
      rotate: { min: 0, max: 360 },
      gravityY: 900 * u,
      ...(realConfetti ? {} : { tint: ITEM_COLORS.slice(0, 8) as unknown as number[] }),
      emitting: false,
    });
    for (const e of [this.dots, this.sparks, this.confettiEm]) e.setDepth(9000);
  }

  private count(n: number): number {
    if (instant()) return 0;
    return reducedMotion() ? Math.ceil(n / 3) : n;
  }

  burst(x: number, y: number, color: number, n = 14): void {
    const c = this.count(n);
    if (!c) return;
    this.dots.setParticleTint(color);
    this.dots.explode(c, x, y);
    this.sparks.explode(Math.ceil(c / 3), x, y);
  }

  sparkle(x: number, y: number, n = 6): void {
    const c = this.count(n);
    if (c) this.sparks.explode(c, x, y);
  }

  confetti(x: number, y: number, n = 60): void {
    const c = this.count(n);
    if (c) this.confettiEm.explode(c, x, y);
  }

  floatText(x: number, y: number, text: string, color = 0xffffff, size = 26): void {
    if (instant()) return;
    const u = vp.u;
    const t = this.scene.add
      .text(x, y, text, {
        fontFamily: FONT,
        fontSize: `${Math.round(size * u)}px`,
        fontStyle: '700',
        color: css(color),
        stroke: '#4a2c2a',
        strokeThickness: Math.round(5 * u),
      })
      .setOrigin(0.5)
      .setDepth(9500);
    this.scene.tweens.add({
      targets: t,
      y: y - 60 * u,
      alpha: { from: 1, to: 0 },
      scale: { from: 0.6, to: 1.1 },
      duration: 900,
      ease: 'Cubic.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  shake(intensity = 0.006, ms = 220): void {
    if (deco(ms)) this.scene.cameras.main.shake(ms, intensity);
  }
}
